import { defineAsyncComponent, markRaw } from 'vue';
import { getNestedSlices } from './nested-slices';
import { withPersonalizationCookie } from './personalization-cookie.js';

export { getNestedSlices };

// Recursive routine for dynamic slices resolution (including `.data.slices[*].name`).
// Used for dynamic slices resolution in pages view components.
export function resolvePageBodySlices(pageBody: any[] = []): string[] {
    const names = new Set<string>();

    if (!Array.isArray(pageBody) || !pageBody.length) {
        return [];
    }

    // Only interested in dynamic slices, starting with `s-`
    const maybeAdd = (name: any) => {
        if (typeof name === 'string' && name.startsWith('s-')) {
            names.add(name);
        }
    };

    const resolveRecursive = (node: any) => {
        maybeAdd(node.name);
        getNestedSlices(node).forEach(resolveRecursive);
    };

    pageBody.forEach(resolveRecursive);

    return Array.from(names);
}

export function defaultSectionResolver(name: string): string {
    if (name.startsWith('s-developer')) return 'developer';
    if (name.startsWith('s-trueimage')) return 'trueimage';
    if (name.startsWith('s-acronis-scs')) return 'scs';
    return 'pages';
}

export interface RegisterSlicesRequest {
    pageBody: any[];
    componentRegistrar: (name: string, component: any) => void;
    sectionResolver?: (fullname: string) => string;
}

/**
 * Register slice components dynamically.
 *
 * @param req.pageBody The `page.body` data
 * @param req.componentRegistrar A function called for each name/component
 * @param req.sectionResolver A function to resolve the section for a given name (defaults to 'pages')
 */
export function registerSlices(req: RegisterSlicesRequest) {
    if (!req || typeof req.componentRegistrar !== 'function') return;

    const { pageBody = [], componentRegistrar, sectionResolver = defaultSectionResolver } = req;

    const names = resolvePageBodySlices(pageBody);

    names.forEach((fullname) => {
        const sliceName = fullname.replace(/^s-/, '');
        const section = sectionResolver(fullname);

        const loader = () => import(`@core/slices/${section}/${sliceName}/${sliceName}.vue`)
            .then((module) => withPersonalizationCookie(module.default));

        try {
            const asyncComponent = markRaw(defineAsyncComponent(loader));
            componentRegistrar(fullname, asyncComponent);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[registerSlices] Failed to register slice: "${fullname}"`, err);
        }
    });
}
